import React, { useEffect, useState } from 'react';
import { useBranding } from '../../context/BrandingContext';

interface BrandMarkProps {
  className?: string;
  /** Pick the mark intended for a dark background. */
  onDark?: boolean;
  /**
   * Shown when the tenant has supplied no logo. Defaults to the product name as
   * text, so a brandless tenant still gets an identity rather than a blank space.
   */
  fallback?: 'text' | 'none';
}

/**
 * The tenant's logo, sized by the caller's className.
 *
 * Replaces the statically imported vendor logo that several pages carried. When a
 * tenant has no logo the product name is rendered instead — never a platform mark,
 * which on a whitelabel domain would be a contract breach rather than a cosmetic
 * bug.
 */
export const BrandMark: React.FC<BrandMarkProps> = ({
  className = 'h-10 w-auto',
  onDark = false,
  fallback = 'text',
}) => {
  const { branding } = useBranding();
  const [failed, setFailed] = useState(false);

  // A tenant can configure a URL that later 404s or is blocked. Reset on change so
  // a corrected URL is retried rather than staying permanently failed.
  useEffect(() => {
    setFailed(false);
  }, [branding.logoLightUrl, branding.logoDarkUrl]);

  const src = onDark
    ? branding.logoDarkUrl || branding.logoLightUrl
    : branding.logoLightUrl || branding.logoDarkUrl;

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={branding.productName || ''}
        className={className}
        loading="eager"
        referrerPolicy="no-referrer"
        // A broken image icon is worse than no logo, and on a whitelabel domain it
        // reads as the product being broken. Fall through to the text mark.
        onError={() => setFailed(true)}
      />
    );
  }

  if (fallback === 'none' || !branding.productName) return null;

  return (
    <span className={`font-bold text-lg ${onDark ? 'text-white' : 'text-agentic-text-primary'}`}>
      {branding.productName}
    </span>
  );
};
